import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, MapPin, DollarSign, Eye, MessageCircle, Leaf, Calendar, Filter } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api-client';
import type { HarvestListing, HarvestCategory } from '@shared/types';

export function MarketplaceHarvestsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter] = useState<string>('available');

  const { data: listings, isLoading } = useQuery({
    queryKey: ['marketplace-harvests', categoryFilter, statusFilter],
    queryFn: () => {
      let url = '/api/listings/harvests?status=' + statusFilter;
      if (categoryFilter !== 'all') {
        url += '&category=' + categoryFilter;
      }
      return api<HarvestListing[]>(url);
    },
  });

  // Filter by search query client-side
  const filteredListings = listings?.filter(listing => 
    searchQuery === '' || 
    listing.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    listing.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Harvest Listings</h1>
          <p className="text-muted-foreground">
            Browse fresh produce and harvest from local farmers
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search harvests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="vegetables">Vegetables</SelectItem>
              <SelectItem value="fruits">Fruits</SelectItem>
              <SelectItem value="grains">Grains</SelectItem>
              <SelectItem value="livestock">Livestock</SelectItem>
              <SelectItem value="dairy">Dairy</SelectItem>
              <SelectItem value="eggs">Eggs</SelectItem>
              <SelectItem value="processed">Processed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Listings Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading harvests...</p>
          </div>
        ) : filteredListings && filteredListings.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map((listing) => (
              <HarvestListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          <Card className="py-12">
            <CardContent className="text-center">
              <p className="text-muted-foreground">No harvests found. Try adjusting your filters.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

interface HarvestListingCardProps {
  listing: HarvestListing;
}

function HarvestListingCard({ listing }: HarvestListingCardProps) {
  const statusBadge = {
    available: <Badge variant="default">Available</Badge>,
    sold_out: <Badge variant="secondary">Sold Out</Badge>,
    expired: <Badge variant="secondary">Expired</Badge>,
    removed: <Badge variant="destructive">Removed</Badge>,
  }[listing.status];

  const categoryLabel = {
    vegetables: 'Vegetables',
    fruits: 'Fruits',
    grains: 'Grains',
    livestock: 'Livestock',
    dairy: 'Dairy',
    eggs: 'Eggs',
    processed: 'Processed',
  }[listing.category];

  const harvestDate = listing.harvestDate 
    ? new Date(listing.harvestDate).toLocaleDateString()
    : null;

  const availableUntil = listing.availableUntil
    ? new Date(listing.availableUntil).toLocaleDateString()
    : null;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between mb-2">
          <Badge variant="secondary">{categoryLabel}</Badge>
          {listing.organic && (
            <Badge variant="outline" className="bg-green-50">
              <Leaf className="h-3 w-3 mr-1" />
              Organic
            </Badge>
          )}
        </div>
        <CardTitle className="text-xl">{listing.productName}</CardTitle>
        <CardDescription className="line-clamp-2">{listing.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-2">
          <div className="flex items-center gap-1 text-lg font-semibold">
            <DollarSign className="h-4 w-4" />
            {listing.currency === 'GHS' ? '₵' : listing.currency} {listing.pricePerUnit.toLocaleString()}
          </div>
          <span className="text-sm text-muted-foreground">per {listing.unit}</span>
        </div>
        
        <div className="text-sm">
          <span className="font-medium">Available: </span>
          <span>{listing.quantity} {listing.unit}</span>
        </div>

        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 flex-shrink-0" />
          {listing.location}
        </div>

        {harvestDate && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            Harvested: {harvestDate}
          </div>
        )}

        {availableUntil && (
          <div className="text-sm">
            <span className="text-muted-foreground">Available until: </span>
            <span className="font-medium">{availableUntil}</span>
          </div>
        )}

        {listing.certifications && listing.certifications.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-1">Certifications:</p>
            <div className="flex flex-wrap gap-1">
              {listing.certifications.map((cert, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {cert}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {listing.views}
          </span>
          <span className="flex items-center gap-1">
            <MessageCircle className="h-4 w-4" />
            {listing.inquiries}
          </span>
        </div>
      </CardContent>
      <CardFooter className="flex gap-2">
        <Button className="flex-1" size="sm" disabled={listing.status !== 'available'}>
          {listing.status === 'available' ? 'View Details' : statusBadge}
        </Button>
        {listing.status === 'available' && (
          <Button variant="outline" size="sm">
            <MessageCircle className="h-4 w-4" />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
